import { Box, Button, Card, DialogActions, DialogContent } from "@mui/material";
import { useSnackbar } from "notistack";
import { Map } from "ol";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { AtonUnit } from "~/api/GeneratedApi";
import { addAtoNsToMap } from "../MapPage/functions/addAtoNsToMap";
import useCreateMap from "../MapPage/hooks/useCreateMap";
import useMapPopup from "../MapPage/hooks/useMapPopup";
import { useMapStyles } from "../MapPage/MapPage";
import MapPopups from "../MapPage/popups/MapPopups";
import { LayerTitle } from "../MapPage/types/layers";

type Props = {
  atonUnits: AtonUnit[];
  fitToExtend: boolean;
  onClose: () => void;
};

export const AtoNsMapDialog = ({
  atonUnits,
  fitToExtend,
  onClose,
}: Props): JSX.Element => {
  const { t } = useTranslation("");
  const { createMap } = useCreateMap();
  const mapRef = useRef<Map>();
  const [mapState, setMapState] = React.useState<Map | undefined>();
  const mapClasses = useMapStyles();
  const mapContainerRef = useRef<HTMLDivElement>();
  const { createPopup, unmountPopup, hoveredItem, selectedItem } =
    useMapPopup();

  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    if (mapContainerRef.current && !mapState) {
      const createdMap = createMap({
        target: mapContainerRef.current,
        existingMap: mapState,
        includedLayers: [LayerTitle.OSM, LayerTitle.AtoNs],
      });
      mapRef.current = createdMap;
      setMapState(createdMap);
    }
  }, [createMap, mapState]);

  React.useEffect(() => {
    if (mapState && atonUnits.length > 0)
      addAtoNsToMap(
        mapState,
        atonUnits,
        fitToExtend,
        enqueueSnackbar,
        t("fairwayReport:noAtonUnitLocations"),
      );
  }, [atonUnits, enqueueSnackbar, fitToExtend, mapState, t]);

  React.useEffect(() => {
    if (mapRef.current) {
      createPopup({ map: mapRef.current });
    }
    return () => {
      unmountPopup();
    };
  }, [createPopup, unmountPopup]);

  return (
    <>
      <Card id="popup" className={mapClasses.popup}>
        {mapRef.current && (
          <MapPopups
            map={mapRef.current}
            selectedItem={selectedItem}
            hoveredItem={hoveredItem}
          />
        )}
      </Card>
      <DialogContent>
        <Box sx={{ width: "100%", height: "100%" }} ref={mapContainerRef} />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t("general:close")}
        </Button>
      </DialogActions>
    </>
  );
};
